import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { TRANSLATIONS } from './translations';
import { syncLangTranslator, retranslateNow } from './DomTranslator';
import { bumpEpoch, subscribe, getStatus, retryNow, fetchStatus, setProtectedNames } from './translationEngine';
import { setUserLocale } from '@/utils/datetime';

const LanguageContext = createContext(null);
// Display-language preference only (also mirrored onto the user profile via
// PUT /api/auth/me/preferences). Japanese is always the safe default.
const STORE_KEY = 'sensbey_lang';

// T2 — 英語表示に切り替えたとき、翻訳してはいけない語（人名・会社名）を一度だけ取る。
// 取れなかった場合は何も訳さない方向に倒れないよう、従来どおりの動作に戻す。
let namesLoaded = false;
function loadProtectedNames() {
  if (namesLoaded) return;
  namesLoaded = true;
  axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/translate/no-translate-terms`)
    .then(({ data }) => {
      setProtectedNames(data?.terms || [], data?.name_en || {});
      retranslateNow();
    })
    .catch(() => { namesLoaded = false; });
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORE_KEY);
      return saved === 'en' ? 'en' : 'ja';   // ja unless a valid saved preference
    } catch { return 'ja'; }
  });
  const [trStatus, setTrStatus] = useState(getStatus());

  useEffect(() => subscribe(setTrStatus), []);

  useEffect(() => {
    // Any in-flight English response from before this switch is discarded.
    bumpEpoch();
    try { document.documentElement.lang = lang; } catch { /* noop */ }
    try { syncLangTranslator(lang); } catch { /* noop */ }
    // Keep the DateTimeService date format (JA "/" vs EN "-") in sync.
    try { setUserLocale(lang); } catch { /* noop */ }
    if (lang === 'en') { fetchStatus(); loadProtectedNames(); } else { setTrStatus(getStatus()); }
  }, [lang]);

  const setLang = useCallback((l) => {
    const next = l === 'en' ? 'en' : 'ja';
    setLangState(next);
    try { localStorage.setItem(STORE_KEY, next); } catch { /* noop */ }
    // Best-effort profile persistence (existing preference mechanism).
    axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me/preferences`,
      { language: next }).catch(() => { /* preference stays local */ });
  }, []);

  const retryTranslation = useCallback(() => {
    retryNow();
    setTrStatus(getStatus());
    retranslateNow();
  }, []);

  const t = useCallback((key, vars) => {
    const entry = TRANSLATIONS[key];
    let str = entry ? (entry[lang] ?? entry.ja ?? key) : key;
    if (vars) {
      Object.keys(vars).forEach((k) => { str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), vars[k]); });
    }
    return str;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{
      lang, setLang, t,
      translationDegraded: lang === 'en' && !!trStatus.degraded,
      translationError: trStatus.lastErrorCategory,
      retryTranslation,
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useT() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // プロバイダー外で描かれた場合も生のキーは出さない（日本語を既定にする）
    const fallbackT = (key, vars) => {
      const entry = TRANSLATIONS[key];
      let str = entry ? (entry.ja ?? key) : key;
      if (vars) {
        Object.keys(vars).forEach((k) => {
          str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), vars[k]);
        });
      }
      return str;
    };
    return { lang: 'ja', setLang: () => {}, t: fallbackT,
             translationDegraded: false, translationError: null,
             retryTranslation: () => {} };
  }
  return ctx;
}
